export function formatFollowerCount(count: number | null): string | null {
  if (count === null) return null;
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return String(count);
}

const NETWORK_LABELS: Record<string, string> = {
  twitter: 'Twitter/X',
  x: 'Twitter/X',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

export function formatNetwork(network: string): string {
  return NETWORK_LABELS[network.toLowerCase()] ?? network;
}

// Only networks with an unambiguous, stable handle-based profile URL scheme.
// LinkedIn is deliberately excluded — /in/ vs /company/ depends on entity
// type in a way this data can't reliably confirm, so guessing would risk
// linking to the wrong profile.
const PROFILE_URL_BUILDERS: Record<string, (handle: string) => string> = {
  twitter: handle => `https://twitter.com/${handle}`,
  x: handle => `https://twitter.com/${handle}`,
  instagram: handle => `https://instagram.com/${handle}`,
  tiktok: handle => `https://www.tiktok.com/@${handle.replace(/^@/, '')}`,
  youtube: handle => `https://www.youtube.com/@${handle.replace(/^@/, '')}`,
  facebook: handle => `https://facebook.com/${handle}`,
};

export function buildProfileUrl(network: string, handle: string | null): string | null {
  if (!handle) return null;
  const builder = PROFILE_URL_BUILDERS[network.toLowerCase()];
  return builder ? builder(handle) : null;
}

export function isLikelyUrl(value: string | null): boolean {
  if (!value) return false;
  return /^https?:\/\/\S+\.\S/i.test(value.trim());
}

export function toHref(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

// Rounds to 2dp before signing to avoid floating-point artifacts like
// "0.19999999999999996" when a value is derived by subtraction.
export function formatSigned(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(2)}`;
}
